import { ReactNode } from "react";
import { isNil } from "lodash";
import { PrintOnly } from "../Common/__styles__/PrintOnly";
import { ScreenOnly } from "../Common/__styles__/ScreenOnly";
import { Body } from "../Common/Typography";
import {
  Fieldset,
  HelperTextSpan,
  HelperTextWrapper,
  Row,
} from "./__styles__/Wrapper";
import Label, { LabelProps } from "./Label";

export type WrapperProps = {
  name: string;
  children: ReactNode;
  label?: undefined | Maybe<string>;
  labelDecorator?: ReactNode;
  error?: Maybe<string>;
  addon?: ReactNode;
  expandWithError?: boolean;
  tooltip?: string;
  compactLabel?: LabelProps["compact"];
  labelSize?: LabelProps["size"];
  helperText?: Maybe<string>;
  printValue?: string;
} & Pick<LabelProps, "description" | "disabled" | "required">;

const HelperText = ({
  errorText,
  defaultText,
}: {
  errorText?: Maybe<string>;
  expandWithError?: boolean;
  defaultText?: Maybe<string>;
}) => {
  if (!errorText && !defaultText) {
    return null;
  }

  return (
    <HelperTextWrapper>
      <HelperTextSpan isError={!!errorText}>
        {errorText ?? defaultText}
      </HelperTextSpan>
    </HelperTextWrapper>
  );
};

export default ({
  label,
  labelDecorator,
  compactLabel = false,
  name,
  error,
  addon,
  children,
  required,
  description,
  disabled,
  expandWithError = true,
  tooltip,
  labelSize,
  helperText,
  printValue,
}: WrapperProps) => {
  return (
    <Fieldset error={!!error}>
      {!!label && (
        <Row>
          <Label
            id={`${name}-label`}
            text={label}
            compact={compactLabel}
            htmlFor={name}
            required={required}
            description={description}
            disabled={disabled}
            size={labelSize}
            tooltip={tooltip}
          />
          {labelDecorator ? labelDecorator : null}
        </Row>
      )}
      {addon}
      {!isNil(printValue) ? <ScreenOnly>{children}</ScreenOnly> : children}
      {!isNil(printValue) && (
        <PrintOnly width="full">
          <Body size={"default"} type={"regular"} testId={`print-only-${name}`}>
            {printValue.trim() || "--"}
          </Body>
        </PrintOnly>
      )}
      <HelperText
        errorText={error}
        expandWithError={expandWithError}
        defaultText={helperText}
      />
    </Fieldset>
  );
};
